// CTSK-351 (folds in CTSK-352) — per-row action palette for the
// native registry-table renderers (Bookmarks first). Mirrors the
// document-detail Tool Palette (CTSK-292) at row scale: a tight
// horizontal strip of small icon+label chips, each declared as one
// `registryFiles[].rowActions[]` entry on the tool manifest.
//
// Contract:
//   - Actions come from the file's `rowActions[]` (validated
//     server-side by RegistryFileEntry.rowActions; see
//     packages/shared/src/schemas/tool.ts).
//   - `requiresField` filters actions per row — an action is skipped
//     entirely when its required field is missing/nullish on the row.
//     This is the "don't render a broken URL" guarantee.
//   - `urlTemplate` is interpolated via template-interpolate.ts —
//     placeholders are URL-encoded, non-string values coerced.
//   - `clipboard:<payload>` URLs are handed to copyToClipboard (the
//     existing http-Vault-safe wrapper); everything else renders as
//     an `<a target="_blank" rel="noopener noreferrer">` (or same-tab
//     when `openInNewTab: false`) so the browser owns navigation.
//
// The parent table decides whether to render an "Actions" column at
// all — if `actions` is empty, this component returns null (the
// column can then be hidden by the parent's own gate). That keeps the
// "no rowActions declared → no empty column artifact" guarantee at
// the caller site rather than duplicating it here.

import { useCallback, useState } from 'react';
import type { RegistryFileEntry } from '@captain-tasko/shared';
import { toast } from './Toast.js';
import { copyToClipboard } from '../lib/clipboard.js';
import {
  interpolate,
  interpolateUrl,
  interpolateBody,
  buildResponseMapPatch,
  decodeClipboardPayload,
  type InterpolateContext,
} from '../lib/template-interpolate.js';

/** Non-null type for one row action entry off the manifest. */
export type RegistryRowAction = NonNullable<RegistryFileEntry['rowActions']>[number];

export interface RegistryRowActionsPaletteProps {
  /** Actions declared for this file on the tool manifest. Empty /
   *  omitted arrays render nothing (component returns null). */
  actions: readonly RegistryRowAction[] | undefined;
  /** The row's entry object — the interpolation source and the
   *  requiresField filter's read target. Passed as a Record so the
   *  palette works across every registry schema (bookmarks-v1,
   *  data-sources-v1, events-v1, …) without a type coupling. */
  entry: Record<string, unknown>;
  /** Optional handler for the operator-clicked action key (slug) —
   *  fires AFTER the palette resolves the URL and either opens it or
   *  copies to clipboard. Useful for downstream analytics; the palette
   *  keeps ownership of the primary side-effect. */
  onActionInvoked?: (slug: string) => void;
  /** CTSK-364 — extra interpolation variables computed by the parent
   *  (first tenant: {captain_tasko_ref}, per-row link back to the
   *  entry in CT). Applies to BOTH interpolation paths; ctx wins over
   *  entry fields on name collision. */
  interpolationCtx?: InterpolateContext;
  /** CTSK-364 — persist a responseMap-built patch onto the entry that
   *  initiated a fetch-and-map action (the parent owns the PATCH
   *  endpoint call — it knows toolId + filePath). Absent → captured
   *  responses are dropped with an error toast, so a fixture/test
   *  caller without write wiring degrades loudly rather than
   *  silently. */
  onPatchEntry?: (entryId: string, patch: Record<string, unknown>) => Promise<unknown>;
  /** CTSK-364 — parent-owned refetch, invoked per the action's
   *  onSuccess setting ('refresh' | 'toast+refresh'). */
  onRefreshRequested?: () => void;
}

/** Read a field value off the entry — tolerant of Date/number/etc.
 *  and treats empty strings as "missing" so `?url=` with an empty
 *  string never reaches the target. */
function fieldPresent(entry: Record<string, unknown>, field: string): boolean {
  const v = entry[field];
  if (v == null) return false;
  if (typeof v === 'string' && v.length === 0) return false;
  return true;
}

export function RegistryRowActionsPalette({
  actions,
  entry,
  onActionInvoked,
  interpolationCtx,
  onPatchEntry,
  onRefreshRequested,
}: RegistryRowActionsPaletteProps) {
  // CTSK-364 — per-action in-flight guard so a slow external tool
  // can't be double-fired from the same row. Keyed by slug (the
  // palette instance is already per-row).
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(new Set());

  const handleClipboardClick = useCallback(
    async (slug: string, payload: string) => {
      const ok = await copyToClipboard(payload);
      if (ok) toast.success(`Copied ${payload.length > 48 ? payload.slice(0, 45) + '…' : payload}`);
      else toast.error('Clipboard write failed');
      onActionInvoked?.(slug);
    },
    [onActionInvoked],
  );

  // CTSK-364 — fetch-and-map executor. Fetches the interpolated URL
  // (optionally POSTing an interpolated JSON body), extracts response
  // fields via responseMap dotted paths, persists them through the
  // parent's onPatchEntry, then applies the onSuccess UX. Every
  // failure path (network / CORS / non-2xx / non-JSON / PATCH error)
  // lands as an error toast and leaves the entry unchanged.
  const handleFetchClick = useCallback(
    async (action: RegistryRowAction) => {
      if (inFlight.has(action.slug)) return;
      setInFlight((prev) => new Set(prev).add(action.slug));
      try {
        const url = interpolateUrl(action.urlTemplate, entry, interpolationCtx);
        const method = action.method ?? 'GET';
        const body = action.bodyTemplate
          ? interpolateBody(action.bodyTemplate, entry, interpolationCtx)
          : undefined;
        // NOTE: direct browser fetch, cross-origin by design (v1 —
        // hyper-scanno serves Access-Control-Allow-Origin; a CT-side
        // proxy is explicitly out of scope per CTSK-364).
        const res = await fetch(url, {
          method,
          headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json: unknown = await res.json().catch(() => null);

        if (action.responseMap && json !== null) {
          const patch = buildResponseMapPatch(action.responseMap, json);
          if (patch) {
            const entryId = typeof entry.id === 'string' ? entry.id : '';
            if (!entryId) {
              throw new Error('entry has no id — response captured but not persistable');
            }
            if (!onPatchEntry) {
              throw new Error('no patch handler wired — response captured but not persisted');
            }
            await onPatchEntry(entryId, patch);
          }
        }

        const onSuccess = action.onSuccess ?? 'toast+refresh';
        if (onSuccess === 'toast' || onSuccess === 'toast+refresh') {
          toast.success(`${action.label}: success`);
        }
        if (onSuccess === 'refresh' || onSuccess === 'toast+refresh') {
          onRefreshRequested?.();
        }
        onActionInvoked?.(action.slug);
      } catch (err) {
        toast.error(`${action.label}: ${(err as Error).message}`);
      } finally {
        setInFlight((prev) => {
          const next = new Set(prev);
          next.delete(action.slug);
          return next;
        });
      }
    },
    [entry, interpolationCtx, onPatchEntry, onRefreshRequested, onActionInvoked, inFlight],
  );

  if (!actions || actions.length === 0) return null;

  // Filter to actions this row can actually run — the requiresField
  // gate is the "don't render a broken URL" guarantee. Actions with
  // no requiresField always render.
  const rowActions = actions.filter((a) => !a.requiresField || fieldPresent(entry, a.requiresField));
  if (rowActions.length === 0) return null;

  return (
    <div
      role="toolbar"
      aria-label="Row actions"
      style={{
        display: 'inline-flex',
        gap: 4,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {rowActions.map((action) => {
        // CTSK-364 — fetch-and-map actions render as buttons that run
        // the executor; everything else keeps the CTSK-351 anchor /
        // clipboard rendering byte-for-byte (backward-compat: absent
        // mode === 'open-in-tab').
        if (action.mode === 'fetch-and-map') {
          const busy = inFlight.has(action.slug);
          const tooltip = `${action.label}\n\n${action.method ?? 'GET'} ${interpolateUrl(action.urlTemplate, entry, interpolationCtx)} (captures response onto this row)`;
          return (
            <button
              key={action.slug}
              type="button"
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => { void handleFetchClick(action); }}
              title={tooltip}
              style={busy ? { ...paletteBtnStyle, opacity: 0.5, cursor: 'progress' } : paletteBtnStyle}
            >
              {action.icon && <IconGlyph name={action.icon} />}
              <span>{busy ? `${action.label}…` : action.label}</span>
            </button>
          );
        }

        const resolved = interpolate(action.urlTemplate, entry);
        const clipboardPayload = decodeClipboardPayload(resolved);
        // Tooltip mirrors ToolPalette: label + short description of
        // what happens on click.
        const tooltip = clipboardPayload
          ? `${action.label}\n\nCopies to clipboard: ${clipboardPayload}`
          : `${action.label}\n\n${resolved} (opens in ${action.openInNewTab === false ? 'this tab' : 'new tab'})`;

        if (clipboardPayload !== null) {
          return (
            <button
              key={action.slug}
              type="button"
              onClick={() => { void handleClipboardClick(action.slug, clipboardPayload); }}
              title={tooltip}
              style={paletteBtnStyle}
            >
              {action.icon && <IconGlyph name={action.icon} />}
              <span>{action.label}</span>
            </button>
          );
        }

        const openInNewTab = action.openInNewTab !== false;
        return (
          <a
            key={action.slug}
            href={resolved}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noopener noreferrer' : undefined}
            onClick={() => onActionInvoked?.(action.slug)}
            title={tooltip}
            style={paletteBtnStyle}
          >
            {action.icon && <IconGlyph name={action.icon} />}
            <span>{action.label}</span>
          </a>
        );
      })}
    </div>
  );
}

// Palette button visual — matches the tool-palette__btn density from
// ToolPalette.tsx (icon 12–13px, tight padding, subtle border). Kept
// as a style object rather than a CSS class so the component ships
// standalone without a globals.css addition.
const paletteBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 8px',
  fontSize: 11,
  fontWeight: 500,
  fontFamily: 'inherit',
  color: 'var(--accent-primary)',
  background: 'color-mix(in srgb, var(--accent-primary) 8%, var(--surface-raised))',
  border: '1px solid color-mix(in srgb, var(--accent-primary) 35%, var(--border-subtle))',
  borderRadius: 'var(--radius-sm, 4px)',
  cursor: 'pointer',
  textDecoration: 'none',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

// Small icon renderer — the palette allows manifest authors to name a
// Lucide icon in `action.icon`, but we deliberately DO NOT bundle the
// full Lucide dynamic import here (would balloon the web bundle for
// every unshown icon). Ships a small allowlist of the icons the
// first-round manifests actually declare (scan / copy / external-link
// / globe / search / link). Unknown names render a neutral glyph so
// operator typos degrade gracefully — no crash, no missing button.
function IconGlyph({ name }: { name: string }) {
  const size = 12;
  const stroke = 'currentColor';
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (name.toLowerCase()) {
    case 'scan':
      return (
        <svg {...common}><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10"/></svg>
      );
    case 'copy':
      return (
        <svg {...common}><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      );
    case 'external-link':
    case 'externallink':
      return (
        <svg {...common}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
      );
    case 'globe':
      return (
        <svg {...common}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
      );
    case 'search':
      return (
        <svg {...common}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      );
    case 'link':
      return (
        <svg {...common}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
      );
    default:
      // Neutral glyph (small filled dot) — obvious enough that the
      // operator notices the typo without the button being unusable.
      return (
        <svg {...common}><circle cx="12" cy="12" r="3" fill={stroke}/></svg>
      );
  }
}

